import { useState } from 'react'
import { Link } from '@tanstack/react-router'

import { ContextMenuButton } from '@/components/ui/context-menu'
import {
  LedgerTable,
  type LedgerColumn,
  type LedgerServerQuery,
} from '@/components/ui/LedgerTable'
import { formatShortRelativeDateTime } from '@/lib/relativeDateTime'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  NewCustomerScreen,
  type NewCustomerInitialValues,
} from '@/screens/NewCustomerScreen'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'

export interface CustomerRow {
  /** Public-facing ID */
  id: string
  /** Private/internal ID */
  privateId?: string
  fullName: string
  externalId?: string
  contactEmail?: string
  contactPhone?: string
  contactAddress?: string
  invoiceName?: string
  invoiceAddress?: string
  invoiceEmail?: string
  invoicePhone?: string
  invoiceVat?: string
  externalCustomerId?: string
  createdAt?: string
  updatedAt?: string
  /** Set on archived customers. */
  archivedAt?: string
  archiveReason?: string
  /** Display name of whoever archived it. */
  archivedByName?: string
  /** Display name of the customer its resources were relinked to. */
  mergedIntoName?: string
}

export type CustomersScope = 'active' | 'archived'

export interface CustomersScreenProps {
  customers: CustomerRow[]
  totalItems: number
  loading?: boolean
  error?: boolean
  onQueryChange: (query: LedgerServerQuery) => void
  pageSize?: number
  onCreateCustomer?: (
    values: NewCustomerInitialValues,
  ) => void | Promise<void>
  onUpdateCustomer?: (
    id: string,
    values: NewCustomerInitialValues,
  ) => void | Promise<void>
  scope?: CustomersScope
  onScopeChange?: (scope: CustomersScope) => void
  /** Opens the archive dialog for this customer. */
  onArchiveCustomer?: (customer: CustomerRow) => void
  onRestoreCustomer?: (id: string) => void | Promise<void>
  onViewCustomer?: (id: string) => void
}

const customerSaveErrorMessage =
  'Unable to save customer. Please try again.'

const customerFieldLabels: Record<string, string> = {
  external_id: 'External ID',
  full_name: 'Full name',
  invoice_address: 'Invoice address',
  invoice_email: 'Invoice email',
  invoice_name: 'Invoice name',
  invoice_phone: 'Invoice phone',
  invoice_vat: 'Invoice VAT',
}

export function CustomersScreen({
  customers,
  totalItems,
  loading,
  error,
  onQueryChange,
  pageSize = 25,
  onCreateCustomer,
  onUpdateCustomer,
  scope = 'active',
  onScopeChange,
  onArchiveCustomer,
  onRestoreCustomer,
  onViewCustomer,
}: CustomersScreenProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [panelCollapsed, setPanelCollapsed] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] =
    useState<'create' | 'edit'>('create')
  const [editingCustomer, setEditingCustomer] =
    useState<CustomerRow | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [restoreError, setRestoreError] = useState<string | null>(null)

  const internalId = (customer: CustomerRow) => customer.privateId ?? customer.id

  const selected =
    customers.find((customer) => internalId(customer) === selectedKey) ??
    customers[0] ??
    null

  const handleAddCustomerClick = () => {
    setDialogMode('create')
    setEditingCustomer(null)
    setFormError(null)
    setIsDialogOpen(true)
  }

  const handleSaveNewCustomer = async (
    values: NewCustomerInitialValues,
  ) => {
    setFormError(null)
    try {
      if (dialogMode === 'create') {
        if (onCreateCustomer) {
          await onCreateCustomer(values)
        }
      } else if (dialogMode === 'edit' && editingCustomer && onUpdateCustomer) {
        await onUpdateCustomer(internalId(editingCustomer), values)
      }
      setIsDialogOpen(false)
    } catch (error) {
      setFormError(
        formatPocketBaseError(error, {
          fallbackMessage: customerSaveErrorMessage,
          fieldLabels: customerFieldLabels,
        }),
      )
    }
  }

  const handleEditCustomer = (customer: CustomerRow) => {
    setDialogMode('edit')
    setEditingCustomer(customer)
    setFormError(null)
    setIsDialogOpen(true)
  }

  const handleRestoreCustomer = async (customer: CustomerRow) => {
    setRestoreError(null)
    try {
      await onRestoreCustomer?.(internalId(customer))
    } catch (error) {
      setRestoreError(
        formatPocketBaseError(error, {
          fallbackMessage: 'Unable to restore this customer.',
        }),
      )
    }
  }

  const columns: LedgerColumn<CustomerRow>[] = [
    {
      key: 'ref',
      label: 'Ref',
      widthClass: 'w-28',
      sortValue: (customer) => customer.id,
      filter: 'text',
      render: (customer) => (
        <Link
          to="/customers/$id"
          params={{ id: customer.id }}
          className="font-mono text-[12.5px] whitespace-nowrap text-blue-700 hover:underline"
        >
          {customer.id}
        </Link>
      ),
    },
    {
      key: 'customer',
      label: 'Customer',
      widthClass: 'w-56',
      sortValue: (customer) => customer.fullName.toLowerCase(),
      filter: 'text',
      render: (customer) => (
        <div className="flex flex-col overflow-hidden">
          <Link
            to="/customers/$id"
            params={{ id: customer.id }}
            className="w-fit max-w-full overflow-hidden font-medium text-ellipsis whitespace-nowrap border-b border-blue-200 text-blue-700 hover:border-blue-700 hover:text-blue-800"
          >
            {customer.fullName}
          </Link>
          {customer.externalId ? (
            <span className="mt-0.5 text-[12px] text-slate-400">
              Omni ID: {customer.externalId}
            </span>
          ) : null}
          {customer.archivedAt ? (
            <span className="mt-0.5 text-[12px] text-rose-600">
              Archived{customer.archivedByName ? ` by ${customer.archivedByName}` : ''}
              {customer.archiveReason ? ` — ${customer.archiveReason}` : ''}
              {customer.mergedIntoName ? ` (relinked to ${customer.mergedIntoName})` : ''}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'contact',
      label: 'Contact',
      widthClass: 'w-52',
      filter: 'text',
      cellClass: 'text-slate-600',
      render: (customer) => (
        <div className="overflow-hidden">
          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
            {customer.contactEmail ?? '—'}
          </div>
          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
            {customer.contactPhone ?? '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'address',
      label: 'Address',
      filter: 'text',
      cellClass: 'text-slate-600',
      render: (customer) => (
        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
          {customer.contactAddress || '—'}
        </div>
      ),
    },
    {
      key: 'invoiceTo',
      label: 'Invoice to',
      widthClass: 'w-44',
      sortValue: (customer) =>
        (customer.invoiceName ?? customer.fullName).toLowerCase(),
      filter: 'text',
      cellClass: 'text-slate-600',
      render: (customer) => (
        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
          {customer.invoiceName ?? customer.fullName}
        </div>
      ),
    },
    {
      key: 'updated',
      label: 'Updated',
      widthClass: 'w-24',
      sortValue: (customer) => customer.updatedAt ?? '',
      defaultSortDir: 'desc',
      cellClass: 'whitespace-nowrap text-[12.5px] text-slate-400',
      render: (customer) =>
        customer.updatedAt
          ? formatShortRelativeDateTime(customer.updatedAt)
          : '—',
    },
    {
      key: 'actions',
      label: '',
      widthClass: 'w-12',
      cellClass: 'text-right',
      render: (customer) => (
        <ContextMenuButton
          items={
            scope === 'archived'
              ? [
                  { id: 'view', label: 'Open' },
                  { id: 'restore', label: 'Restore customer' },
                ]
              : [
                  { id: 'view', label: 'Open' },
                  { id: 'edit', label: 'Edit details' },
                  {
                    id: 'archive',
                    label: 'Archive customer',
                    destructive: true,
                  },
                ]
          }
          aria-label={`Customer actions for ${customer.fullName}`}
          onSelect={(id) => {
            if (id === 'view') {
              onViewCustomer?.(customer.id)
            } else if (id === 'edit') {
              handleEditCustomer(customer)
            } else if (id === 'archive') {
              onArchiveCustomer?.(customer)
            } else if (id === 'restore') {
              void handleRestoreCustomer(customer)
            }
          }}
        />
      ),
    },
  ]

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="mx-auto flex max-w-[1720px] items-start gap-4 px-4 py-6">
        <div className="min-w-0 flex-1">
          <div role="tablist" aria-label="Customer list" className="mb-3 flex gap-1 border-b border-slate-200">
            {(['active', 'archived'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={scope === tab}
                onClick={() => onScopeChange?.(tab)}
                className={`-mb-px cursor-pointer border-b-2 px-3 py-1.5 text-[13px] font-medium ${
                  scope === tab
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab === 'active' ? 'Customers' : 'Archived'}
              </button>
            ))}
          </div>
          <LedgerTable
            rows={customers}
            columns={columns}
            rowKey={internalId}
            itemLabelPlural="customers"
            emptyLabel={
              scope === 'archived'
                ? 'No customers have been archived.'
                : 'No customers have been captured yet.'
            }
            title="Customers"
            description="Saved customers with default contact and invoicing details."
            onRowClick={(customer) => setSelectedKey(internalId(customer))}
            rowHighlight={(customer) =>
              selected && internalId(customer) === internalId(selected)
                ? 'bg-blue-50'
                : undefined
            }
            defaultSort={{ key: 'updated', dir: 'desc' }}
            initialPageSize={pageSize}
            server={{ totalItems, loading, onQueryChange }}
            mobileSearchKey="customer"
            mobileCard={(customer) => (
              <div className="space-y-1 px-3.5 py-3">
                <div className="flex items-center justify-between gap-2">
                  <Link
                    to="/customers/$id"
                    params={{ id: customer.id }}
                    className="min-w-0 truncate font-medium text-blue-700"
                  >
                    {customer.fullName}
                  </Link>
                  <span className="shrink-0 font-mono text-[12px] text-slate-400">
                    {customer.id}
                  </span>
                </div>
                <div className="text-[12.5px] text-slate-600">
                  {customer.contactPhone ? (
                    <a
                      href={`tel:${customer.contactPhone}`}
                      className="text-blue-700"
                    >
                      {customer.contactPhone}
                    </a>
                  ) : (
                    <span className="text-slate-400">No phone</span>
                  )}
                  {customer.contactEmail ? (
                    <span className="text-slate-500"> · {customer.contactEmail}</span>
                  ) : null}
                </div>
                {customer.updatedAt ? (
                  <div className="text-[12px] text-slate-400">
                    Updated {formatShortRelativeDateTime(customer.updatedAt)}
                  </div>
                ) : null}
              </div>
            )}
            actions={
              scope === 'active' ? (
                <button
                  type="button"
                  className="inline-flex h-8 cursor-pointer items-center rounded-lg bg-blue-600 px-3.5 text-[12.5px] font-medium text-white shadow-sm hover:bg-blue-700"
                  onClick={handleAddCustomerClick}
                >
                  Add customer
                </button>
              ) : null
            }
          />
          {error ? (
            <p className="mt-2 text-sm text-rose-600">
              Unable to load customers right now. Adjust the search or try
              again.
            </p>
          ) : null}
          {restoreError ? (
            <p className="mt-2 text-sm text-rose-600">{restoreError}</p>
          ) : null}
        </div>

        {selected && panelCollapsed ? (
          <div className="sticky top-4 hidden w-[46px] shrink-0 flex-col items-center gap-3 self-start rounded-lg border border-slate-200 bg-white pt-3 pb-4 lg:flex">
            <button
              type="button"
              onClick={() => setPanelCollapsed(false)}
              title="Expand details"
              className="h-[30px] w-[30px] cursor-pointer rounded-lg border border-slate-300 bg-white text-[15px] leading-none text-slate-700"
            >
              ‹
            </button>
            <div className="max-h-[60vh] overflow-hidden text-[11px] tracking-wide whitespace-nowrap text-ellipsis text-slate-500 [writing-mode:vertical-rl]">
              {selected.id} · {selected.fullName}
            </div>
          </div>
        ) : null}

        {selected && !panelCollapsed ? (
          <aside className="sticky top-4 hidden max-h-[calc(100dvh-110px)] w-96 shrink-0 flex-col self-start overflow-hidden rounded-lg border border-slate-200 bg-white lg:flex">
            {/* Header */}
            <div className="shrink-0 border-b border-slate-100 px-5 pt-[18px] pb-3.5">
              <div className="flex items-start justify-between gap-2.5">
                <div className="min-w-0">
                  <div className="mb-2 flex items-center gap-2">
                    <Link
                      to="/customers/$id"
                      params={{ id: selected.id }}
                      className="font-mono text-xs font-medium text-blue-700 hover:underline"
                    >
                      {selected.id}
                    </Link>
                  </div>
                  <h2 className="m-0 text-base font-semibold">
                    {selected.fullName}
                  </h2>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Link
                    to="/customers/$id"
                    params={{ id: selected.id }}
                    title="Open customer page"
                    className="inline-flex h-7 items-center gap-1 rounded-[7px] border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Open
                    <span aria-hidden className="text-[10px]">
                      ↗
                    </span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => setPanelCollapsed(true)}
                    title="Collapse details"
                    className="h-7 w-7 cursor-pointer rounded-[7px] border border-slate-200 bg-white text-sm leading-none text-slate-500"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {/* Contact */}
              <div className="border-b border-slate-100 px-5 py-3.5">
                <h3 className="m-0 mb-2 text-[11px] font-semibold tracking-wider text-slate-600 uppercase">
                  Contact
                </h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <div>
                    <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                      Email
                    </div>
                    <div className="text-[12.5px] break-words text-slate-900">
                      {selected.contactEmail || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                      Phone
                    </div>
                    <div className="text-[12.5px] text-slate-900">
                      {selected.contactPhone || '—'}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                      Address
                    </div>
                    <div className="text-[12.5px] leading-snug text-slate-900">
                      {selected.contactAddress || '—'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoicing */}
              <div className="border-b border-slate-100 px-5 py-3.5">
                <h3 className="m-0 mb-2 text-[11px] font-semibold tracking-wider text-slate-600 uppercase">
                  Invoicing
                </h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <div>
                    <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                      Invoice to
                    </div>
                    <div className="text-[12.5px] text-slate-900">
                      {selected.invoiceName ?? selected.fullName}
                    </div>
                  </div>
                  <div>
                    <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                      VAT
                    </div>
                    <div className="text-[12.5px] text-slate-900">
                      {selected.invoiceVat || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                      Omni ID
                    </div>
                    <div className="text-[12.5px] text-slate-900">
                      {selected.externalId || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                      Email
                    </div>
                    <div className="text-[12.5px] break-words text-slate-900">
                      {selected.invoiceEmail || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                      Phone
                    </div>
                    <div className="text-[12.5px] text-slate-900">
                      {selected.invoicePhone || '—'}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                      Address
                    </div>
                    <div className="text-[12.5px] leading-snug text-slate-900">
                      {selected.invoiceAddress || '—'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Record meta */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-5 py-3.5">
                <div>
                  <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                    Created
                  </div>
                  <div className="text-[12.5px] text-slate-600">
                    {selected.createdAt
                      ? formatShortRelativeDateTime(selected.createdAt)
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                    Updated
                  </div>
                  <div className="text-[12.5px] text-slate-600">
                    {selected.updatedAt
                      ? formatShortRelativeDateTime(selected.updatedAt)
                      : '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            {selected.archivedAt ? (
              <div className="shrink-0 space-y-2 border-t border-slate-100 px-5 py-3">
                <p className="text-[12.5px] text-rose-600">
                  Archived{selected.archivedByName ? ` by ${selected.archivedByName}` : ''}
                  {selected.archiveReason ? ` — ${selected.archiveReason}` : ''}
                </p>
                {selected.mergedIntoName ? (
                  <p className="text-[12.5px] text-slate-500">
                    Resources relinked to {selected.mergedIntoName}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => void handleRestoreCustomer(selected)}
                  className="h-8 w-full cursor-pointer rounded-lg border border-slate-300 bg-white text-[12.5px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  Restore customer
                </button>
              </div>
            ) : (
              <div className="flex shrink-0 gap-2 border-t border-slate-100 px-5 py-3">
                <button
                  type="button"
                  onClick={() => handleEditCustomer(selected)}
                  className="h-8 flex-1 cursor-pointer rounded-lg border border-slate-300 bg-white text-[12.5px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  Edit details
                </button>
                <button
                  type="button"
                  onClick={() => onArchiveCustomer?.(selected)}
                  className="h-8 cursor-pointer rounded-lg border border-rose-200 bg-white px-3 text-[12.5px] font-medium text-rose-600 hover:bg-rose-50"
                >
                  Archive
                </button>
              </div>
            )}
          </aside>
        ) : null}

        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            setIsDialogOpen(open)
            if (!open) {
              setFormError(null)
            }
          }}
        >
          <DialogContent
	    className="max-w-3xl max-h-[90vh] max-h-[90dvh] overflow-y-auto top-[10vh] top-[10dvh] translate-y-0 p-0 pb-[calc(4rem+env(safe-area-inset-bottom))]">
	    <DialogTitle className="pt-4 text-center text-gray-400" > Customer </DialogTitle>
	    <DialogDescription className="px-6 text-center">
  Add a new customer (or edit existing)
	    </DialogDescription>
            <NewCustomerScreen
              key={
                dialogMode === 'edit' && editingCustomer
                  ? `edit-${editingCustomer.id}`
                  : 'create'
              }
              mode={dialogMode}
              showHeader={false}
              onSave={handleSaveNewCustomer}
              onCancel={() => {
                setIsDialogOpen(false)
                setFormError(null)
              }}
              errorMessage={formError}
              initialValues={
                dialogMode === 'edit' && editingCustomer
                  ? {
                      fullName: editingCustomer.fullName,
                      externalId: editingCustomer.externalId,
                      contactAddress: editingCustomer.contactAddress,
                      contactEmail: editingCustomer.contactEmail,
                      contactPhone: editingCustomer.contactPhone,
                      invoiceName:
                        editingCustomer.invoiceName ??
                        editingCustomer.fullName,
                      invoiceAddress: editingCustomer.invoiceAddress,
                      invoiceEmail: editingCustomer.invoiceEmail,
                      invoicePhone: editingCustomer.invoicePhone,
                      invoiceVat: editingCustomer.invoiceVat,
                      externalCustomerId: editingCustomer.externalCustomerId,
                    }
                  : undefined
              }
            />
          </DialogContent>
        </Dialog>
      </main>
    </div>
  )
}

export const demoCustomers: CustomerRow[] = [
  {
    id: 'cust-1',
    fullName: 'John Doe',
    externalId: 'CUST-12345',
    contactEmail: 'john.doe@example.com',
    contactPhone: '+1 555 123 4567',
    contactAddress: '123 Main Road, Business Park, Unit 4B',
    invoiceName: 'John Doe',
  },
  {
    id: 'cust-2',
    fullName: 'Acme Corp Facilities',
    externalId: 'CUST-98765',
    contactEmail: 'facilities@acmecorp.com',
    contactPhone: '+1 555 987 6543',
    contactAddress: '45 Industrial Road, Warehouse 12',
    invoiceName: 'Acme Corp Accounts',
  },
  {
    id: 'cust-3',
    fullName: 'Sunrise Apartments Body Corporate',
    externalId: 'CUST-55555',
    contactEmail: 'trustees@sunrise-apartments.co',
    contactPhone: '+1 555 246 8100',
    contactAddress: '78 Coastal View Drive',
    invoiceName: 'Sunrise Apartments BC',
  },
]
